import {
  CHAT_THREAD_DIALOG_CLASS_NAME,
  CHAT_DIALOG_DESCRIPTION_CLASS_NAME,
} from "@/features/chat/chat-dialog-styles";
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ChatThreadBody } from "./chat-thread-body";
import { useChatFormAccess } from "./hooks/use-chat-form-access";
import { ORG_CHAT_THREADS_QUERY_KEY } from "./org-chat-threads-query-key";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AlertCircle, Loader2 } from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { useOrganization } from "@/hooks/use-organization";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { queryClient } from "@/ajax/queryClient";

// Main Chat Dialog Component
export function ChatDialog({
  isOpen,
  onOpenChange,
  onRestoreFocus,
  user,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onRestoreFocus?: () => void;
  user: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  };
}) {
  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();
  const { data: currentUser } = useQuery(trpc.user.getUser.queryOptions());
  const { canManageJobPosts, documentSigningEnabled } =
    useChatFormAccess(currentUser);
  const {
    data: thread,
    isLoading: isThreadLoading,
    error: threadError,
  } = useQuery(
    trpc.chat.orgGetThreadByUserId.queryOptions({
      userId: user.id,
      organizationBusinessUnitId: selectedOrgBusinessUnit?.id,
    }),
  );

  const invalidateConversation = async (threadId: string) => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: trpc.chat.orgGetThreadByUserId.queryKey({
          userId: user.id,
          organizationBusinessUnitId: selectedOrgBusinessUnit?.id,
        }),
      }),
      queryClient.invalidateQueries(
        trpc.chat.listChatThreadMessages.queryOptions({ threadId }),
      ),
      queryClient.invalidateQueries({
        queryKey: trpc.chat.getUnreadMessagesCount.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: [ORG_CHAT_THREADS_QUERY_KEY],
      }),
    ]);
  };

  const { mutateAsync: sendMessage } = useMutation(
    trpc.chat.sendMessage.mutationOptions({
      networkMode: "always",
      onSuccess: async (_data, { threadId }) => {
        await invalidateConversation(threadId);
      },
    }),
  );
  const { mutateAsync: createThread } = useMutation(
    trpc.chat.orgCreateChatThread.mutationOptions({
      onSuccess: async (newThread) => {
        await invalidateConversation(newThread.id);
      },
    }),
  );
  const {
    data: messages,
    isLoading: isMessagesLoading,
    error: messagesError,
    refetch: refetchMessages,
  } = useQuery(
    trpc.chat.listChatThreadMessages.queryOptions(
      {
        threadId: thread?.id ?? "",
      },
      {
        staleTime: 0,
        refetchInterval: () => {
          return 5_000 + Math.random() * 5_000;
        },
        enabled: !!thread?.id,
      },
    ),
  );

  const isLoading = isThreadLoading || (!!thread?.id && isMessagesLoading);
  const error = threadError || messagesError;

  const handleSendMessage = async (
    message: string,
    attachmentIds?: string[],
  ) => {
    let threadId = thread?.id;
    if (!threadId) {
      const newThread = await createThread({
        userId: user.id,
        organizationBusinessUnitId: selectedOrgBusinessUnit?.id,
      });
      threadId = newThread.id;
    }
    await sendMessage({
      threadId,
      message,
      attachmentIds,
    });
  };

  const providerDisplayName =
    [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
    undefined;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={CHAT_THREAD_DIALOG_CLASS_NAME}
        onCloseAutoFocus={(event) => {
          if (!onRestoreFocus) {
            return;
          }
          event.preventDefault();
          onRestoreFocus();
        }}
      >
        <DialogHeader className="shrink-0 border-b border-slate-200 bg-white px-4 py-4 pr-14 text-left sm:px-5 sm:pr-14">
          <div className="flex min-w-0 items-center gap-3">
            <Avatar className="h-11 w-11 shrink-0 rounded-xl">
              <AvatarImage src="" alt={`${user.firstName} ${user.lastName}`} />
              <AvatarFallback className="rounded-xl border border-primary/10 bg-primary/5 font-semibold text-primary">
                {user.firstName?.[0]}
                {user.lastName?.[0]}
              </AvatarFallback>
            </Avatar>
            <DialogTitle className="min-w-0 text-base font-semibold leading-6 text-slate-900 [overflow-wrap:anywhere]">
              {user.firstName} {user.lastName}
            </DialogTitle>
          </div>
          <DialogDescription className={CHAT_DIALOG_DESCRIPTION_CLASS_NAME}>
            Messages and shared documents
          </DialogDescription>
        </DialogHeader>
        {/* Message Thread Section */}
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="flex flex-col items-center text-center">
              <Loader2 className="h-8 w-8 text-primary animate-spin mb-3" />
              <p className="text-slate-600">Loading conversation...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <Alert variant="destructive" className="max-w-md">
              <AlertCircle className="h-5 w-5 mr-2" />
              <AlertDescription className="text-sm">
                There was an error loading the conversation. Please try again.
              </AlertDescription>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => refetchMessages()}
              >
                Retry
              </Button>
            </Alert>
          </div>
        ) : (
          <ChatThreadBody
            messages={messages ?? []}
            onSendMessage={handleSendMessage}
            threadId={thread?.id ?? ""}
            viewMode="org"
            user={currentUser ? { is_admin: currentUser.is_admin } : undefined}
            organizationBusinessUnitId={selectedOrgBusinessUnit?.id}
            recipientUserId={user.id}
            canManageJobPosts={canManageJobPosts}
            documentSigningEnabled={documentSigningEnabled}
            providerDisplayName={providerDisplayName}
            onFormAssignmentSent={invalidateConversation}
            endedRelationshipLookup={
              selectedOrgBusinessUnit?.id
                ? {
                    organizationBusinessUnitId: selectedOrgBusinessUnit.id,
                    providerUserId: user.id,
                  }
                : undefined
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
